// src/site-addon/configurations/index.ts
import z4 from "zod";

// src/site-addon/configurations/filter.ts
import { z } from "zod";

// src/utils/index.ts
import { customAlphabet } from "nanoid";
function generateSlug(name) {
  return name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
var nanoid = customAlphabet(
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
  16
);
function generateId() {
  return nanoid();
}

// src/site-addon/configurations/filter.ts
var filterSiteAddonConfigurationPaginationTypeSchema = z.enum([
  "simple",
  "loadMore",
  "scroll"
]);
var filterSiteAddonConfigurationPaginationSimpleSchema = z.object({
  type: z.literal(filterSiteAddonConfigurationPaginationTypeSchema.Enum.simple),
  pageSize: z.coerce.number().min(1),
  loop: z.boolean(),
  useWebflowNativeQueryParam: z.boolean()
});
var filterSiteAddonConfigurationPaginationLoadMoreSchema = z.object({
  type: z.literal(
    filterSiteAddonConfigurationPaginationTypeSchema.Enum.loadMore
  ),
  pageSize: z.coerce.number().min(1)
});
var filterSiteAddonConfigurationPaginationScrollSchema = z.object({
  type: z.literal(filterSiteAddonConfigurationPaginationTypeSchema.Enum.scroll),
  pageSize: z.coerce.number().min(1),
  delay: z.coerce.number().min(0)
});
var filterSiteAddonConfigurationPaginationSchema = z.discriminatedUnion("type", [
  filterSiteAddonConfigurationPaginationSimpleSchema,
  filterSiteAddonConfigurationPaginationLoadMoreSchema,
  filterSiteAddonConfigurationPaginationScrollSchema
]);
var filterSiteAddonConfigurationListSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  webflowCollectionId: z.string().nullable(),
  loadExtraItems: z.boolean(),
  pagination: z.nullable(filterSiteAddonConfigurationPaginationSchema)
});
var filterSiteAddonConfigurationSingleInputTypeSchema = z.enum([
  "text",
  "button",
  "checkbox",
  "webflowCheckbox",
  "radio",
  "radioGroup",
  "webflowRadio",
  "webflowRadioGroup",
  "select"
]);
var filterSiteAddonConfigurationMultiInputTypeSchema = z.enum([
  "buttonGroup",
  "webflowDropdown",
  "checkboxGroup",
  "webflowCheckboxGroup"
]);
function isMultiInputType(input) {
  return filterSiteAddonConfigurationMultiInputTypeSchema.safeParse(input).success;
}
var filterSiteAddonConfigurationMultiInputOperatorSchema = z.enum([
  "and",
  "or"
]);
var filterSiteAddonConfigurationInputTypeSchema = z.union([
  filterSiteAddonConfigurationSingleInputTypeSchema,
  filterSiteAddonConfigurationMultiInputTypeSchema
]);
var filterSiteAddonConfigurationInputBaseSchema = z.object({
  id: z.string(),
  name: z.string().min(1)
});
var filterSiteAddonConfigurationSingleInputSchema = filterSiteAddonConfigurationInputBaseSchema.extend({
  type: filterSiteAddonConfigurationSingleInputTypeSchema
});
var filterSiteAddonConfigurationMultiInputSchema = filterSiteAddonConfigurationInputBaseSchema.extend({
  type: filterSiteAddonConfigurationMultiInputTypeSchema,
  maxActiveCount: z.preprocess(
    (value) => value === "" || value === void 0 ? null : value,
    z.coerce.number().min(1).nullable()
  ),
  operator: filterSiteAddonConfigurationMultiInputOperatorSchema
});
var filterSiteAddonConfigurationInputSchema = z.discriminatedUnion(
  "type",
  [
    filterSiteAddonConfigurationSingleInputSchema,
    filterSiteAddonConfigurationMultiInputSchema
  ]
);
function isMultiInputConfig(input) {
  return isMultiInputType(input.type);
}
var filterSortReservedFieldSlugs = z.enum(["default", "text"]);
function isFilterSortReservedFieldSlug(slug) {
  return filterSortReservedFieldSlugs.safeParse(slug).success;
}
var filterTextReservedFieldSlug = "text";
var filterItemInteractionsReservedFieldSlugs = z.enum([
  "liked",
  "rated",
  "disliked",
  "favorited",
  "progress",
  "rating",
  "like_count",
  "dislike_count",
  "favorite_count",
  "started_viewing",
  "viewed_count"
]);
var filterItemInteractionsReservedFieldTypeBySlug = {
  liked: "boolean",
  rated: "boolean",
  disliked: "boolean",
  favorited: "boolean",
  progress: "number",
  rating: "number",
  like_count: "number",
  dislike_count: "number",
  favorite_count: "number",
  started_viewing: "number",
  viewed_count: "number"
};
function isFilterFilterReservedFieldSlug(slug) {
  return slug === filterTextReservedFieldSlug || filterItemInteractionsReservedFieldSlugs.safeParse(slug).success;
}
function isFilterReservedFieldSlug(slug) {
  return isFilterFilterReservedFieldSlug(slug) || isFilterSortReservedFieldSlug(slug);
}
var filterSiteAddonConfigurationSimpleFieldValueTypeSchema = z.enum([
  "keyword",
  "number",
  "date",
  "boolean"
]);
var filterSiteAddonConfigurationFieldBaseSchema = z.object({
  id: z.string(),
  name: z.string().min(1).refine(
    (name) => {
      const slug = generateSlug(name);
      return !isFilterReservedFieldSlug(slug);
    },
    {
      message: "This name is reserved. Slugs like text, liked, like_count, etc. are used internally."
    }
  )
});
var filterSiteAddonConfigurationSimpleFieldSchema = filterSiteAddonConfigurationFieldBaseSchema.extend({
  type: z.literal("simple"),
  valueType: filterSiteAddonConfigurationSimpleFieldValueTypeSchema
});
var filterSiteAddonConfigurationMultiReferenceFieldSchema = filterSiteAddonConfigurationFieldBaseSchema.extend({
  type: z.literal("multiReference"),
  webflowCollectionId: z.string().min(1),
  multiReferenceFieldSlug: z.string().min(1),
  nestedCollectionId: z.string().min(1)
});
var filterSiteAddonConfigurationFieldSchema = z.discriminatedUnion(
  "type",
  [
    filterSiteAddonConfigurationSimpleFieldSchema,
    filterSiteAddonConfigurationMultiReferenceFieldSchema
  ]
);
var filterSiteAddonConfigurationSchema = z.object({
  name: z.string().min(1),
  lists: z.array(filterSiteAddonConfigurationListSchema),
  inputs: z.array(filterSiteAddonConfigurationInputSchema),
  fields: z.array(filterSiteAddonConfigurationFieldSchema),
  backgroundCommands: z.array(z.string().min(1))
});

// src/site-addon/configurations/item-interactions.ts
import { z as z2 } from "zod";
var IS_ITEM_INTERACTIONS_RATING_RELEASED = false;
var IS_ITEM_INTERACTIONS_COMMENTING_RELEASED = false;
var itemInteractionsSiteAddonConfigurationSchema = z2.object({
  webflowCollectionId: z2.string(),
  favoritingConfiguration: z2.object({
    enabled: z2.boolean(),
    favoritedCountWebflowFieldSlug: z2.string().nullable().default(null)
  }),
  likingConfiguration: z2.object({
    enabled: z2.boolean(),
    likedCountWebflowFieldSlug: z2.string().nullable().default(null),
    dislikedCountWebflowFieldSlug: z2.string().nullable().default(null)
  }),
  viewingConfiguration: z2.object({
    enabled: z2.boolean(),
    startedCountWebflowFieldSlug: z2.string().nullable().default(null),
    viewedCountWebflowFieldSlug: z2.string().nullable().default(null)
  }),
  ratingConfiguration: z2.object({
    enabled: z2.boolean(),
    averageRatingWebflowFieldSlug: z2.string().nullable().default(null)
  }),
  commentingConfiguration: z2.object({
    enabled: z2.boolean(),
    commentsCountWebflowFieldSlug: z2.string().nullable().default(null)
  })
});

// src/site-addon/configurations/test.ts
import { z as z3 } from "zod";
var testSiteAddonConfigurationSchema = z3.object({});

// src/site-addon/configurations/index.ts
function makeOneConfigurationSiteAddonSchema(baseShape) {
  const itemInteractionsSchema = baseShape.extend({
    type: z4.literal("item_interactions"),
    configuration: itemInteractionsSiteAddonConfigurationSchema
  });
  const filterSchema = baseShape.extend({
    type: z4.literal("filter"),
    configuration: filterSiteAddonConfigurationSchema
  });
  const testSchema = baseShape.extend({
    type: z4.literal("test"),
    configuration: testSiteAddonConfigurationSchema
  });
  const unionSchema = z4.discriminatedUnion("type", [
    itemInteractionsSchema,
    filterSchema,
    testSchema
  ]);
  return {
    unionSchema,
    itemInteractionsSchema,
    filterSchema,
    testSchema
  };
}
function makeTwoConfigurationsSiteAddonSchema(baseSchema) {
  const itemInteractionsSchema = baseSchema.extend({
    type: z4.literal("item_interactions"),
    productionConfiguration: itemInteractionsSiteAddonConfigurationSchema,
    stagingConfiguration: itemInteractionsSiteAddonConfigurationSchema
  });
  const filterSchema = baseSchema.extend({
    type: z4.literal("filter"),
    productionConfiguration: filterSiteAddonConfigurationSchema,
    stagingConfiguration: filterSiteAddonConfigurationSchema
  });
  const testSchema = baseSchema.extend({
    type: z4.literal("test"),
    productionConfiguration: testSiteAddonConfigurationSchema,
    stagingConfiguration: testSiteAddonConfigurationSchema
  });
  const unionSchema = z4.discriminatedUnion("type", [
    itemInteractionsSchema,
    filterSchema,
    testSchema
  ]);
  return {
    unionSchema,
    itemInteractionsSchema,
    filterSchema,
    testSchema
  };
}

// src/date.ts
import { z as z5 } from "zod";
var timestampDtoSchema = z5.union([z5.date(), z5.string().datetime()]).transform((v) => typeof v === "string" ? v : v.toISOString());

// src/site-addon/dtos/index.ts
import { z as z6 } from "zod";
var baseSiteAddonDtoSchema = z6.object({
  id: z6.string(),
  webflowSiteId: z6.string(),
  name: z6.string(),
  enabled: z6.boolean(),
  createdAt: timestampDtoSchema,
  updatedAt: timestampDtoSchema
});
var {
  unionSchema: siteAddonDtoSchema,
  itemInteractionsSchema: itemInteractionsSiteAddonDtoSchema,
  filterSchema: filterSiteAddonDtoSchema,
  testSchema: testSiteAddonDtoSchema
} = makeTwoConfigurationsSiteAddonSchema(baseSiteAddonDtoSchema);
var createSiteAddonDtoSchema = z6.object({
  siteAddon: siteAddonDtoSchema
});
var updateSiteAddonDtoSchema = z6.object({
  siteAddon: siteAddonDtoSchema
});

// src/site-addon/enums/index.ts
import z7 from "zod";
var siteAddonTypeEnum = z7.enum([
  "item_interactions",
  "filter",
  "test"
]);

// src/site-addon/payloads/index.ts
import { z as z8 } from "zod";
var { unionSchema: createSiteAddonPayloadSchema } = makeTwoConfigurationsSiteAddonSchema(
  z8.object({
    webflowSiteId: z8.string(),
    name: z8.string()
  })
);
var { unionSchema: updateSiteAddonPayloadSchema } = makeTwoConfigurationsSiteAddonSchema(
  z8.object({
    siteAddonId: z8.string(),
    name: z8.string()
  })
);
var setSiteAddonActiveByIdPayloadSchema = z8.object({
  siteAddonId: z8.string(),
  active: z8.boolean()
});
var deleteSiteAddonByIdPayloadSchema = z8.object({
  siteAddonId: z8.string()
});

// src/consumables/dtos/index.ts
import z9 from "zod";
var webflowSiteConfigDtoSchema = z9.object({
  siteAddons: z9.array(siteAddonDtoSchema)
});
var getWebflowSiteConfigByWebflowSiteIdDtoSchema = z9.object({
  siteConfig: webflowSiteConfigDtoSchema
});
var relationIndexEntryDtoSchema = z9.object({
  webflowCollectionId: z9.string(),
  nestedCollectionId: z9.string(),
  multiReferenceFieldSlug: z9.string(),
  parentSlugs: z9.array(z9.string()),
  parentIndexesByNestedName: z9.record(z9.string(), z9.array(z9.number().int()))
});
var relationIndexDtoSchema = relationIndexEntryDtoSchema;
var getRelationIndexByWebflowSiteIdDtoSchema = z9.object({
  relationIndex: relationIndexDtoSchema
});
var consumeConsumerItemInteractionsDataDtoSchema = z9.object({
  likes: z9.array(z9.string()),
  dislikes: z9.array(z9.string()),
  favorites: z9.array(z9.string()),
  progresses: z9.record(z9.number()),
  ratings: z9.record(z9.number()),
  commented: z9.array(z9.string())
});
var getConsumerItemInteractionsDtoSchema = z9.object({
  consumerItemInteractions: consumeConsumerItemInteractionsDataDtoSchema.partial()
});

// src/consumables/enums/index.ts
import z10 from "zod";
var interactionDataTypeSchema = z10.enum([
  "likes",
  "dislikes",
  "favorites",
  "progresses",
  "ratings",
  "commented"
]);

// src/consumables/payloads/index.ts
import z13 from "zod";

// src/consumables/payloads/get-consumer-item-interactions.ts
import z11 from "zod";
var getConsumerItemInteractionsPayloadSchema = z11.object({
  dataTypes: z11.array(interactionDataTypeSchema)
});

// src/consumables/payloads/upsert-consumer-item-interactions.ts
import z12 from "zod";
var interactionTypeSchema = z12.enum([
  "like",
  "dislike",
  "unreact",
  "favorite",
  "unfavorite",
  "progress",
  "resetProgress",
  "rate",
  "unrate",
  "comment",
  "uncomment"
]);
function makeInteractionSchema(type, schema) {
  return z12.object({
    type: z12.literal(type),
    payload: schema
  });
}
var upsertConsumerItemInteractionsPayloadSchema = z12.object({
  webflowCollectionItemSlug: z12.string(),
  interactions: z12.array(
    z12.discriminatedUnion("type", [
      makeInteractionSchema("like", z12.void()),
      makeInteractionSchema("dislike", z12.void()),
      makeInteractionSchema("unreact", z12.void()),
      makeInteractionSchema("favorite", z12.void()),
      makeInteractionSchema("unfavorite", z12.void()),
      makeInteractionSchema("progress", z12.number().min(0).max(1)),
      makeInteractionSchema("resetProgress", z12.void()),
      makeInteractionSchema("rate", z12.number().int().min(1)),
      makeInteractionSchema("unrate", z12.void()),
      makeInteractionSchema("comment", z12.string().min(1)),
      makeInteractionSchema("uncomment", z12.void())
    ])
  )
});

// src/consumables/payloads/index.ts
var getWebflowSiteConfigByWebflowSiteIdPayloadSchema = z13.object({
  webflowSiteId: z13.string()
});
var getRelationIndexByWebflowSiteIdPayloadSchema = z13.object({
  webflowCollectionId: z13.string(),
  nestedCollectionId: z13.string(),
  multiReferenceFieldSlug: z13.string()
});

// src/organization/dtos/index.ts
import z14 from "zod";
var organizationDtoSchema = z14.object({
  id: z14.string(),
  name: z14.string(),
  slug: z14.string()
});
var organizationMonthDailyConsumptionDtoSchema = z14.object({
  month: z14.string(),
  /** Visitors of the busiest site, which is what the plan limit applies to. */
  visitors: z14.number(),
  /** Sites without any visitor for the month are omitted. */
  visitorsByWebflowSiteId: z14.record(z14.string(), z14.number()),
  createdAt: timestampDtoSchema
});
var organizationConsumptionDtoSchema = z14.object({
  siteAddons: z14.number(),
  currentMonthDailyConsumptions: z14.array(
    organizationMonthDailyConsumptionDtoSchema
  ),
  monthlyConsumptionsForLastTwelveMonths: z14.array(
    organizationMonthDailyConsumptionDtoSchema
  )
});
var getOrganizationConsumptionDtoSchema = z14.object({
  organizationConsumption: organizationConsumptionDtoSchema
});

// src/organization/payloads/index.ts
import z15 from "zod";
var linkWebflowWorkspaceToOrganizationPayloadSchema = z15.object({
  webflowWorkspaceId: z15.string()
});
var getSubscribeUrlPayloadSchema = z15.object({
  plan: z15.enum(["launch", "grow", "scale", "business"])
});

// src/subscription/dtos/index.ts
import z16 from "zod";
var activeSubscriptionDtoSchema = z16.object({
  id: z16.string(),
  plan: z16.string(),
  referenceId: z16.string(),
  stripeCustomerId: z16.string().nullable(),
  stripeSubscriptionId: z16.string().nullable(),
  status: z16.enum(["active", "trialing"]),
  periodStart: z16.date().nullable(),
  periodEnd: z16.date().nullable(),
  cancelAtPeriodEnd: z16.boolean(),
  seats: z16.number().nullable(),
  trialStart: z16.date().nullable(),
  trialEnd: z16.date().nullable()
});

// src/webflow-collection/dtos/index.ts
import z17 from "zod";
var webflowCollectionDtoSchema = z17.object({
  id: z17.string(),
  webflowSiteId: z17.string(),
  displayName: z17.string(),
  singularName: z17.string(),
  slug: z17.string(),
  listened: z17.boolean(),
  createdAt: timestampDtoSchema,
  updatedAt: timestampDtoSchema.nullable()
});
var getWebflowCollectionByIdDtoSchema = z17.object({
  webflowCollection: webflowCollectionDtoSchema
});

// src/webflow-collection/payloads/index.ts
import z18 from "zod";
var getWebflowCollectionByIdPayloadSchema = z18.object({
  webflowCollectionId: z18.string()
});

// src/webflow-site/dtos/index.ts
import z19 from "zod";
var webflowSiteDtoSchema = z19.object({
  id: z19.string(),
  displayName: z19.string(),
  shortName: z19.string(),
  previewUrl: z19.string().nullable(),
  /**
   * Set when the owner removed the script this App had applied.
   *
   * The nightly rollout leaves those sites alone, so the dashboard has to say
   * so — otherwise the site quietly stops getting updates with no explanation.
   */
  relkitScriptRemovedAt: timestampDtoSchema.nullable(),
  createdAt: timestampDtoSchema,
  updatedAt: timestampDtoSchema.nullable(),
  publishedAt: timestampDtoSchema.nullable()
});

// src/webflow-workspace/dtos/index.ts
import z20 from "zod";
var webflowWorkspaceDtoSchema = z20.object({
  id: z20.string()
});

// src/overviews/organization.ts
import z21 from "zod";
var organizationOverviewDtoSchema = z21.object({
  organization: organizationDtoSchema,
  hasWebflowAccessToken: z21.boolean(),
  subscription: activeSubscriptionDtoSchema.nullable(),
  limits: z21.object({
    siteAddons: z21.number(),
    monthlyVisitors: z21.number()
  }),
  webflowWorkspace: webflowWorkspaceDtoSchema.nullable(),
  webflowSites: z21.array(
    webflowSiteDtoSchema.pick({
      id: true,
      displayName: true
    })
  ),
  webflowCollections: z21.array(
    webflowCollectionDtoSchema.pick({
      id: true,
      webflowSiteId: true,
      displayName: true
    })
  )
});
var getActiveOrganizationOverviewDtoSchema = z21.object({
  organizationOverview: organizationOverviewDtoSchema.nullable()
});

// src/overviews/site-addon.ts
import { z as z22 } from "zod";
var siteAddonOverviewDtoSchema = z22.object({
  webflowSite: webflowSiteDtoSchema.pick({
    id: true,
    displayName: true
  }),
  siteAddon: siteAddonDtoSchema
});
var getSiteAddonOverviewByIdDtoSchema = z22.object({
  siteAddonOverview: siteAddonOverviewDtoSchema
});
var getSiteAddonOverviewByIdPayloadSchema = z22.object({
  siteAddonId: z22.string()
});

// src/webflow-custom-domain/dtos/index.ts
import { z as z23 } from "zod";
var webflowCustomDomainDtoSchema = z23.object({
  id: z23.string(),
  webflowSiteId: z23.string(),
  url: z23.string(),
  publishedAt: timestampDtoSchema.nullable()
});

// src/overviews/webflow-site.ts
import z24 from "zod";
var webflowSiteOverviewDtoSchema = z24.object({
  webflowSite: webflowSiteDtoSchema,
  webflowCustomDomains: z24.array(webflowCustomDomainDtoSchema),
  siteAddons: z24.array(siteAddonDtoSchema)
});
var getWebflowSiteOverviewByIdDtoSchema = z24.object({
  webflowSiteOverview: webflowSiteOverviewDtoSchema
});
var getWebflowSiteOverviewByIdPayloadSchema = z24.object({
  webflowSiteId: z24.string()
});

// src/plans.ts
var planIds = ["free", "launch", "grow", "scale", "business"];
var planById = {
  free: {
    id: "free",
    price: 0,
    stripePriceId: "",
    limits: {
      siteAddons: 3,
      monthlyVisitors: 5e3
    }
  },
  launch: {
    id: "launch",
    price: 18.99,
    //yearlyPrice: 14.99,
    stripePriceId: "price_1Tj3o4GHam6FRsa9sZPXFzJy",
    limits: {
      siteAddons: 5,
      monthlyVisitors: 5e4
    }
  },
  grow: {
    id: "grow",
    price: 38.99,
    //yearlyPrice: 29.99,
    stripePriceId: "price_1Tj3s0GHam6FRsa9N8llxxy6",
    limits: {
      siteAddons: 15,
      monthlyVisitors: 1e5
    }
  },
  scale: {
    id: "scale",
    price: 78.99,
    //yearlyPrice: 59.99,
    stripePriceId: "price_1Tj3scGHam6FRsa9lLv5YA02",
    limits: {
      siteAddons: 30,
      monthlyVisitors: 3e5
    }
  },
  business: {
    id: "business",
    price: 148.99,
    //yearlyPrice: 129.99,
    stripePriceId: "price_1Tj3tEGHam6FRsa98hiJDHnh",
    limits: {
      siteAddons: 60,
      monthlyVisitors: 1e6
    }
  }
};
function resolveOrganizationLimits(planId, overrides) {
  const planLimits = planById[planId].limits;
  return {
    siteAddons: Math.max(
      planLimits.siteAddons,
      overrides.siteAddons ?? planLimits.siteAddons
    ),
    monthlyVisitors: Math.max(
      planLimits.monthlyVisitors,
      overrides.monthlyVisitors ?? planLimits.monthlyVisitors
    )
  };
}
function getPlanState(targetPlan, currentPlan = "free") {
  if (currentPlan === targetPlan) {
    return "current";
  }
  return planIds.indexOf(currentPlan) < planIds.indexOf(targetPlan) ? "upgrade" : "downgrade";
}

// src/relkit-script/index.ts
var relkitScriptPins = {
  "https://cdn.relkit.com": {
    version: "1.0.54",
    integrityHash: "sha256-+fY/k8ILKDMVqKDSO3HR762tGVBRq5urvEHCLZ1HmjQ="
  },
  "https://dev-cdn.relkit.com": {
    version: "1.0.54",
    integrityHash: "sha256-DTnSIu/fqkp4V/bT3QRQFxpZBPTsD5DnA/JYEjKBvyY="
  }
};
var relkitScriptDisplayName = "Relkit";
function normalizeCdnHost(cdnHost) {
  return cdnHost.replace(/\/+$/, "");
}
function resolveRelkitScriptPin(cdnHost) {
  const pin = relkitScriptPins[normalizeCdnHost(cdnHost)];
  if (!pin) {
    throw new Error(
      `No script build is pinned for ${cdnHost}. Add an entry to relkitScriptPins.`
    );
  }
  if (!pin.version || !pin.integrityHash) {
    throw new Error(
      `The script build pinned for ${cdnHost} is empty. Publish a build from the branch serving that CDN, then run: pnpm --filter @relkit/scripts release`
    );
  }
  return pin;
}
var relkitScriptAttributes = {
  siteId: "data-rk-site-id",
  scriptVersion: "data-rk-script-version"
};
function relkitScriptVersionPath(version) {
  return `v${version}`;
}
function buildRelkitScriptUrl(cdnHost, version) {
  return `${normalizeCdnHost(cdnHost)}/${relkitScriptVersionPath(version)}/relkit.js`;
}

// src/user/dtos/index.ts
import z25 from "zod";
var userDtoSchema = z25.object({
  id: z25.string(),
  email: z25.string().email(),
  firstName: z25.string(),
  lastName: z25.string().nullable(),
  image: z25.string().nullable(),
  role: z25.string().nullable(),
  createdAt: timestampDtoSchema,
  updatedAt: timestampDtoSchema
});
var getMeDtoSchema = z25.object({
  user: userDtoSchema
});

// src/webflow/dtos/index.ts
import z26 from "zod";
var webflowSdkCollectionDtoSchema = z26.object({
  id: z26.string(),
  slug: z26.string(),
  displayName: z26.string(),
  singularName: z26.string(),
  createdAt: timestampDtoSchema,
  updatedAt: timestampDtoSchema.nullable()
});
var webflowCollectionDetailsDtoSchema = webflowSdkCollectionDtoSchema.extend({
  fields: z26.array(
    z26.object({
      id: z26.string(),
      slug: z26.string(),
      type: z26.string(),
      isRequired: z26.boolean(),
      displayName: z26.string(),
      helpText: z26.string().nullable(),
      validations: z26.unknown().optional()
    })
  )
});
var getWebflowCollectionsByWebflowSiteIdDtoSchema = z26.object({
  webflowCollections: z26.array(webflowSdkCollectionDtoSchema)
});
var getWebflowCollectionDetailsByIdDtoSchema = z26.object({
  webflowCollectionDetails: webflowCollectionDetailsDtoSchema
});
var createWebflowCollectionFieldByWebflowCollectionIdDtoSchema = z26.object({
  fieldSlug: z26.string()
});
var getPendingWebflowAccessTokenDetailsByWebflowWorkspaceIdDtoSchema = z26.object({
  webflowWorkspaceTokenDetails: z26.object({
    webflowSites: z26.array(
      webflowSiteDtoSchema.pick({
        id: true,
        displayName: true
      })
    )
  })
});

// src/webflow/payloads/index.ts
import z27 from "zod";
var callbackWebflowPayloadSchema = z27.object({
  code: z27.string(),
  state: z27.string().optional()
}).or(
  z27.object({
    error: z27.string(),
    error_description: z27.string()
  })
);
var getWebflowCollectionsByWebflowSiteIdPayloadSchema = z27.object({
  webflowSiteId: z27.string()
});
var getWebflowCollectionDetailsByIdPayloadSchema = z27.object({
  webflowCollectionId: z27.string()
});
var createWebflowCollectionFieldByWebflowCollectionIdPayloadSchema = z27.object({
  webflowCollectionId: z27.string(),
  options: z27.object({
    type: z27.enum([
      "Color",
      "DateTime",
      "Email",
      "File",
      "Image",
      "Link",
      "MultiImage",
      "Number",
      "Phone",
      "PlainText",
      "RichText",
      "Switch",
      "VideoLink"
    ]),
    isRequired: z27.boolean(),
    displayName: z27.string(),
    helpText: z27.string().optional()
  })
});
var syncWebflowCollectionByIdPayloadSchema = z27.object({
  webflowSiteId: z27.string(),
  webflowCollectionId: z27.string(),
  force: z27.union([
    z27.boolean(),
    z27.string().refine((s) => s === "true" || s === "false").transform((s) => s === "true")
  ]).optional()
});
var syncAllWebflowCollectionsByWebflowSiteIdPayloadSchema = z27.object({
  webflowSiteId: z27.string()
});
var getPendingWebflowAccessTokenDetailsByWebflowWorkspaceIdPayloadSchema = z27.object({
  webflowWorkspaceId: z27.string()
});
export {
  IS_ITEM_INTERACTIONS_COMMENTING_RELEASED,
  IS_ITEM_INTERACTIONS_RATING_RELEASED,
  buildRelkitScriptUrl,
  callbackWebflowPayloadSchema,
  consumeConsumerItemInteractionsDataDtoSchema,
  createSiteAddonDtoSchema,
  createSiteAddonPayloadSchema,
  createWebflowCollectionFieldByWebflowCollectionIdDtoSchema,
  createWebflowCollectionFieldByWebflowCollectionIdPayloadSchema,
  deleteSiteAddonByIdPayloadSchema,
  filterItemInteractionsReservedFieldSlugs,
  filterItemInteractionsReservedFieldTypeBySlug,
  filterSiteAddonConfigurationFieldBaseSchema,
  filterSiteAddonConfigurationFieldSchema,
  filterSiteAddonConfigurationInputBaseSchema,
  filterSiteAddonConfigurationInputSchema,
  filterSiteAddonConfigurationInputTypeSchema,
  filterSiteAddonConfigurationListSchema,
  filterSiteAddonConfigurationMultiInputOperatorSchema,
  filterSiteAddonConfigurationMultiInputSchema,
  filterSiteAddonConfigurationMultiInputTypeSchema,
  filterSiteAddonConfigurationMultiReferenceFieldSchema,
  filterSiteAddonConfigurationPaginationLoadMoreSchema,
  filterSiteAddonConfigurationPaginationSchema,
  filterSiteAddonConfigurationPaginationScrollSchema,
  filterSiteAddonConfigurationPaginationSimpleSchema,
  filterSiteAddonConfigurationPaginationTypeSchema,
  filterSiteAddonConfigurationSchema,
  filterSiteAddonConfigurationSimpleFieldSchema,
  filterSiteAddonConfigurationSimpleFieldValueTypeSchema,
  filterSiteAddonConfigurationSingleInputSchema,
  filterSiteAddonConfigurationSingleInputTypeSchema,
  filterSiteAddonDtoSchema,
  filterSortReservedFieldSlugs,
  filterTextReservedFieldSlug,
  generateId,
  generateSlug,
  getActiveOrganizationOverviewDtoSchema,
  getConsumerItemInteractionsDtoSchema,
  getConsumerItemInteractionsPayloadSchema,
  getMeDtoSchema,
  getOrganizationConsumptionDtoSchema,
  getPendingWebflowAccessTokenDetailsByWebflowWorkspaceIdDtoSchema,
  getPendingWebflowAccessTokenDetailsByWebflowWorkspaceIdPayloadSchema,
  getPlanState,
  getRelationIndexByWebflowSiteIdDtoSchema,
  getRelationIndexByWebflowSiteIdPayloadSchema,
  getSiteAddonOverviewByIdDtoSchema,
  getSiteAddonOverviewByIdPayloadSchema,
  getSubscribeUrlPayloadSchema,
  getWebflowCollectionByIdDtoSchema,
  getWebflowCollectionByIdPayloadSchema,
  getWebflowCollectionDetailsByIdDtoSchema,
  getWebflowCollectionDetailsByIdPayloadSchema,
  getWebflowCollectionsByWebflowSiteIdDtoSchema,
  getWebflowCollectionsByWebflowSiteIdPayloadSchema,
  getWebflowSiteConfigByWebflowSiteIdDtoSchema,
  getWebflowSiteConfigByWebflowSiteIdPayloadSchema,
  getWebflowSiteOverviewByIdDtoSchema,
  getWebflowSiteOverviewByIdPayloadSchema,
  interactionDataTypeSchema,
  interactionTypeSchema,
  isFilterFilterReservedFieldSlug,
  isFilterReservedFieldSlug,
  isFilterSortReservedFieldSlug,
  isMultiInputConfig,
  isMultiInputType,
  itemInteractionsSiteAddonConfigurationSchema,
  itemInteractionsSiteAddonDtoSchema,
  linkWebflowWorkspaceToOrganizationPayloadSchema,
  makeOneConfigurationSiteAddonSchema,
  makeTwoConfigurationsSiteAddonSchema,
  organizationConsumptionDtoSchema,
  organizationDtoSchema,
  organizationMonthDailyConsumptionDtoSchema,
  organizationOverviewDtoSchema,
  planById,
  planIds,
  relationIndexDtoSchema,
  relationIndexEntryDtoSchema,
  relkitScriptAttributes,
  relkitScriptDisplayName,
  relkitScriptPins,
  relkitScriptVersionPath,
  resolveOrganizationLimits,
  resolveRelkitScriptPin,
  setSiteAddonActiveByIdPayloadSchema,
  siteAddonDtoSchema,
  siteAddonOverviewDtoSchema,
  siteAddonTypeEnum,
  syncAllWebflowCollectionsByWebflowSiteIdPayloadSchema,
  syncWebflowCollectionByIdPayloadSchema,
  testSiteAddonDtoSchema,
  updateSiteAddonDtoSchema,
  updateSiteAddonPayloadSchema,
  upsertConsumerItemInteractionsPayloadSchema,
  userDtoSchema,
  webflowCollectionDetailsDtoSchema,
  webflowCollectionDtoSchema,
  webflowCustomDomainDtoSchema,
  webflowSdkCollectionDtoSchema,
  webflowSiteConfigDtoSchema,
  webflowSiteDtoSchema,
  webflowSiteOverviewDtoSchema,
  webflowWorkspaceDtoSchema
};
//# sourceMappingURL=index.js.map